// Cookiebot consent and its Google Consent Mode v2 mapping (#1458). The apps
// reuse the Cookiebot domain group behind the www.reed.ai banner, but drive
// Consent Mode themselves (data-consentmode="disabled") so the mapping is
// explicit and testable.

export type ConsentState = 'granted' | 'denied';

export interface ConsentDecision {
  /** True once the visitor has answered the banner, on this or an earlier visit. */
  hasResponse: boolean;
  statistics: boolean;
  marketing: boolean;
  consentId?: string;
  /** When the visitor made the decision, when Cookiebot records it. */
  decidedAt?: Date;
}

export interface GoogleConsent {
  analytics_storage: ConsentState;
  ad_storage: ConsentState;
  ad_user_data: ConsentState;
  ad_personalization: ConsentState;
}

export const DENIED_CONSENT: GoogleConsent = {
  analytics_storage: 'denied',
  ad_storage: 'denied',
  ad_user_data: 'denied',
  ad_personalization: 'denied',
};

/** Statistics → analytics_storage; marketing → the three advertising signals. */
export function googleConsentFor(decision: ConsentDecision | null): GoogleConsent {
  const analytics: ConsentState =
    decision?.hasResponse && decision.statistics ? 'granted' : 'denied';
  const ads: ConsentState = decision?.hasResponse && decision.marketing ? 'granted' : 'denied';

  return {
    analytics_storage: analytics,
    ad_storage: ads,
    ad_user_data: ads,
    ad_personalization: ads,
  };
}

function decisionTime(decision: ConsentDecision): number | null {
  const time = decision.decidedAt?.getTime();
  return time !== undefined && Number.isFinite(time) ? time : null;
}

export function sameDecision(a: ConsentDecision | null, b: ConsentDecision | null): boolean {
  if (!a || !b) {
    return a === b;
  }
  return (
    a.hasResponse === b.hasResponse &&
    a.statistics === b.statistics &&
    a.marketing === b.marketing &&
    a.consentId === b.consentId &&
    decisionTime(a) === decisionTime(b)
  );
}

/** Whether any tag may load at all (basic mode: nothing before consent). */
export function allowsTags(decision: ConsentDecision | null): boolean {
  return Boolean(decision?.hasResponse && (decision.statistics || decision.marketing));
}
